import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for handling hero video play/pause functionality
 */
export default class HeroVideos extends MessageBus {
  /**
   * Constructor function of the HeroVideos class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.observer = null;

    this.init();
  }

  /**
   * Initializes the view by setting up event handlers
   * @returns {HeroVideos} A reference to the current instance of the class
   */
  init() {
    this.cacheDomReferences().enable();
    this.addScrollPaddingEffect();
    this.setupContentRevealTrigger();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {HeroVideos} A reference to the current instance of the class
   */
  cacheDomReferences() {
    this.videos = this.element.querySelectorAll(
      `.${CLASS_NAMES.HERO_HOME_VIDEO}, .${CLASS_NAMES.HERO_CAMPAIGN_VIDEO}`
    );

    this.wrapper = this.element.querySelector(
      `.${CLASS_NAMES.HERO_HOME_WRAPPER}`
    );
    this.mobileImg = this.element.querySelector(
      `.${CLASS_NAMES.HERO_HOME_MOBILE_IMG}`
    );
    this.textWrapper = this.element.querySelector(
      `.${CLASS_NAMES.HERO_CONTENT}`
    );

    this.playButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.HERO_HOME_VIDEO_PLAY}, .${CLASS_NAMES.HERO_CAMPAIGN_VIDEO_PLAY}, .${CLASS_NAMES.HERO_PLAY}`
    );

    this.pauseButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.HERO_HOME_VIDEO_PAUSE}, .${CLASS_NAMES.HERO_CAMPAIGN_VIDEO_PAUSE}, .${CLASS_NAMES.HERO_PAUSE}`
    );

    this.primaryButton = this.element.querySelector(
      `.${CLASS_NAMES.BUTTON_PRIMARY}`
    );
    this.heroCampaignWrapper = this.element.querySelector(
      `.${CLASS_NAMES.HERO_CAMPAIGN_WRAPPER}`
    );

    return this;
  }

  /**
   * Enables event listeners for video play/pause
   * @returns {HeroVideos} A reference to the current instance of the class
   */
  enable() {
    this.playButtons.forEach((button) => {
      button.addEventListener(EVENTS.CLICK, HeroVideos.handlePlay);
      button.addEventListener(EVENTS.KEY_DOWN, (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          HeroVideos.handlePlay(event);
        }
      });
    });
    this.pauseButtons.forEach((button) => {
      button.addEventListener(EVENTS.CLICK, HeroVideos.handlePause);
      button.addEventListener(EVENTS.KEY_DOWN, (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          HeroVideos.handlePause(event);
        }
      });
    });

    if (this.primaryButton && this.heroCampaignWrapper) {
      const hoverImg = this.heroCampaignWrapper.getAttribute(
        SELECTORS.DATA_HOVERIMG
      );
      const originalImg = this.heroCampaignWrapper.style.backgroundImage;

      this.primaryButton.addEventListener(EVENTS.MOUSEENTER, () => {
        if (hoverImg) {
          this.heroCampaignWrapper.style.backgroundImage = `url('${hoverImg}')`;
        }
      });

      this.primaryButton.addEventListener(EVENTS.MOUSELEAVE, () => {
        this.heroCampaignWrapper.style.backgroundImage = originalImg;
      });
    }

    return this;
  }

  setupContentRevealTrigger() {
    if (!this.textWrapper) return;

    this.textWrapper.classList.add(CLASS_NAMES.ANIMATE);
  }

  /**
   * Sets up Intersection Observer to pause videos when out of view.
   */
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.video.play();
          } else {
            this.video.pause();
          }
        });
      },
      {
        root: null,
        threshold: 0.5
      }
    );

    this.videos.forEach((video) => {
      this.observer.observe(video);
    });
  }

  /**
   * Adds padding when the user starts scrolling
   */
  addScrollPaddingEffect() {
    const heroSection = this.element.closest(`.${CLASS_NAMES.HERO_HOME}`);

    if (!heroSection) return;

    window.addEventListener(EVENTS.SCROLL, () => {
      if (window.scrollY > 0) {
        heroSection.classList.add(CLASS_NAMES.HERO_HOME_PADDED);
      } else {
        heroSection.classList.remove(CLASS_NAMES.HERO_HOME_PADDED);
      }
    });
  }

  /**
   * Handles play button click
   * @param {Event} event - the event object
   */
  static handlePlay(event) {
    const video = HeroVideos.getVideoElement(event.target);
    if (video) {
      video.play();
      HeroVideos.toggleButtons(event.target, true);
    }
  }

  /**
   * Handles pause button click
   * @param {Event} event - the event object
   */
  static handlePause(event) {
    const video = HeroVideos.getVideoElement(event.target);
    if (video) {
      video.pause();
      HeroVideos.toggleButtons(event.target, false);
    }
  }

  /**
   * Retrieves the corresponding video element for a given button
   * @param {HTMLElement} button - The clicked play/pause button
   * @returns {HTMLVideoElement|null} - The related video element or null if not found
   */
  static getVideoElement(button) {
    return (
      button
        .closest(`.${CLASS_NAMES.HERO_HOME_WRAPPER}`)
        ?.querySelector(`.${CLASS_NAMES.HERO_HOME_VIDEO}`) ||
      button
        .closest(`.${CLASS_NAMES.HERO_CAMPAIGN_WRAPPER}`)
        ?.querySelector(`.${CLASS_NAMES.HERO_CAMPAIGN_VIDEO}`) ||
      document.getElementById(`${CLASS_NAMES.HERO_VIDEO_JS}`)
    );
  }

  /**
   * Toggles play/pause buttons visibility
   * @param {HTMLElement} button - The clicked button
   * @param {boolean} isPlaying - Whether the video is playing
   */
  static toggleButtons(button, isPlaying) {
    const wrapper = button.closest(
      `.${CLASS_NAMES.HERO_HOME_WRAPPER}, .${CLASS_NAMES.HERO_CAMPAIGN_WRAPPER}, .${CLASS_NAMES.HERO_VIDEO}`
    );

    if (wrapper) {
      const playButton = wrapper.querySelector(
        `.${CLASS_NAMES.HERO_HOME_VIDEO_PLAY}, .${CLASS_NAMES.HERO_CAMPAIGN_VIDEO_PLAY}, .${CLASS_NAMES.HERO_PLAY}`
      );
      const pauseButton = wrapper.querySelector(
        `.${CLASS_NAMES.HERO_HOME_VIDEO_PAUSE}, .${CLASS_NAMES.HERO_CAMPAIGN_VIDEO_PAUSE}, .${CLASS_NAMES.HERO_PAUSE}`
      );

      if (playButton && pauseButton) {
        if (isPlaying) {
          playButton.style.display = 'none';
          pauseButton.style.display = 'inline-block';
        } else {
          playButton.style.display = 'inline-block';
          pauseButton.style.display = 'none';
        }
      }
    }
  }
}
